import { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle, AlertTriangle, ExternalLink, Loader2 } from 'lucide-react';
import { api } from '../../context/AuthContext';
import { useProject } from '../../context/ProjectContext';

function ConsistencyBadge({ value }) {
  if (value == null || value === '') {
    return <span className="text-slate-400 dark:text-slate-500">Not applicable</span>;
  }
  const s = String(value).toLowerCase();
  if (s === 'match') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300">
        <CheckCircle2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
        Matches your business info
      </span>
    );
  }
  if (s.includes('mismatch')) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-900 dark:bg-amber-950/50 dark:text-amber-200">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
        {value}
      </span>
    );
  }
  return (
    <span className="text-sm text-slate-700 dark:text-slate-300">{value}</span>
  );
}

function CitationResults({ data }) {
  const rows = Array.isArray(data.citations) ? data.citations : [];
  const foundCount = rows.filter((r) => r.found).length;

  return (
    <div className="space-y-4">
      {data.success !== false && (
        <p className="text-sm text-slate-600 dark:text-slate-400">
          We checked {rows.length} citation source{rows.length === 1 ? '' : 's'}. Your business appears on{' '}
          <span className="font-semibold text-slate-800 dark:text-slate-200">{foundCount}</span> of them.
        </p>
      )}
      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-800">
        <table className="w-full min-w-[520px] text-left text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-800 dark:bg-slate-900/80 dark:text-slate-400">
              <th scope="col" className="px-4 py-3 font-medium">
                Directory / source
              </th>
              <th scope="col" className="px-4 py-3 font-medium">
                Found
              </th>
              <th scope="col" className="px-4 py-3 font-medium">
                Listing
              </th>
              <th scope="col" className="px-4 py-3 font-medium">
                Info consistency
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
            {rows.map((row, i) => (
              <tr key={`${row.source}-${i}`} className="bg-white dark:bg-slate-950">
                <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">{row.source ?? '—'}</td>
                <td className="px-4 py-3">
                  {row.found ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
                      <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />
                      Yes
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-slate-500 dark:text-slate-400">
                      <XCircle className="h-4 w-4 shrink-0" aria-hidden />
                      No
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {row.url ? (
                    <a
                      href={row.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-violet-600 hover:underline dark:text-violet-400"
                    >
                      View listing
                      <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                    </a>
                  ) : (
                    <span className="text-slate-400 dark:text-slate-500">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <ConsistencyBadge value={row.consistency} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NAPResults({ data }) {
  const score = typeof data.nap_score === 'number' ? data.nap_score : null;
  const issues = Array.isArray(data.issues) ? data.issues : [];

  let scoreLabel = 'Overall NAP consistency';
  let scoreTone = 'text-slate-800 dark:text-slate-200';
  if (score != null) {
    if (score >= 80) scoreTone = 'text-emerald-700 dark:text-emerald-400';
    else if (score >= 50) scoreTone = 'text-amber-700 dark:text-amber-400';
    else scoreTone = 'text-red-700 dark:text-red-400';
  }

  return (
    <div className="space-y-4">
      {score != null && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/50">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">{scoreLabel}</p>
          <p className={`mt-1 text-3xl font-bold tabular-nums ${scoreTone}`}>{score}</p>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Out of 100. Higher means your name, address, and phone match more consistently across the web.</p>
        </div>
      )}
      {issues.length > 0 && (
        <div>
          <h4 className="mb-2 text-sm font-semibold text-slate-800 dark:text-slate-200">Items to review</h4>
          <ul className="space-y-2">
            {issues.map((issue, i) => (
              <li
                key={i}
                className="flex gap-2 rounded-md border border-amber-200 bg-amber-50/80 px-3 py-2 text-sm text-amber-950 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-100"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
                <span>{issue}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {score == null && issues.length === 0 && (
        <p className="text-sm text-slate-500 dark:text-slate-400">No score or issues were returned for this audit.</p>
      )}
    </div>
  );
}

function ResultsPanel({ data }) {
  if (!data || typeof data !== 'object') return null;

  if (Array.isArray(data.citations)) {
    return <CitationResults data={data} />;
  }
  if (typeof data.nap_score === 'number' || Array.isArray(data.issues)) {
    return <NAPResults data={data} />;
  }

  return (
    <div className="space-y-2">
      <p className="text-sm text-slate-600 dark:text-slate-400">Here is the raw response (unrecognized format):</p>
      <pre className="max-h-96 overflow-auto rounded-md bg-slate-50 p-4 text-xs text-slate-700 dark:bg-slate-900 dark:text-slate-300">
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
}

export default function LocalSEOPage() {
  const { selectedProjectId } = useProject();
  const [activeTab, setActiveTab] = useState('citations');
  const [businessName, setBusinessName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [website, setWebsite] = useState('');
  const [status, setStatus] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const errDetail = (err) => {
    const d = err.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) return d.map((x) => x.msg || x).join(', ');
    return err.message || 'Request failed';
  };

  const handleCitations = async (e) => {
    e.preventDefault();
    if (!businessName.trim()) {
      setStatus('Error: Business name is required');
      return;
    }
    setLoading(true);
    setData(null);
    setStatus('Discovering citations across directories — this can take up to a minute...');
    try {
      const { data: res } = await api.post('/local-seo/discover-citations', {
        business_name: businessName,
        phone,
        zip_code: zipCode,
        website_url: website,
        project_id: selectedProjectId || undefined,
      });
      setStatus('Discovered successfully!');
      setData(res);
    } catch (error) {
      setStatus(`Error: ${errDetail(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleNAP = async (e) => {
    e.preventDefault();
    if (!businessName.trim()) {
      setStatus('Error: Business name is required');
      return;
    }
    setLoading(true);
    setData(null);
    setStatus('Checking NAP consistency...');
    try {
      const { data: res } = await api.post('/local-seo/check-nap', {
        business_name: businessName,
        phone,
        address,
        website_url: website,
        project_id: selectedProjectId || undefined,
      });
      setStatus('Audit completed!');
      setData(res);
    } catch (error) {
      setStatus(`Error: ${errDetail(error)}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div className="space-y-6 p-4 md:p-6" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Local SEO & Citations</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">Citation discovery and NAP consistency checks.</p>
      </div>

      <div className="flex gap-4 border-b border-slate-200 dark:border-slate-800">
        {[
          { id: 'citations', label: 'Citation Discovery' },
          { id: 'nap', label: 'NAP Audit' },
          { id: 'locations', label: 'Locations' },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            className={`pb-2 px-1 text-sm ${activeTab === t.id ? 'border-b-2 border-violet-600 font-medium text-violet-600' : 'text-slate-500'}`}
            onClick={() => {
              setActiveTab(t.id);
              setData(null);
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Business Name</label>
            <input
              type="text"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
              placeholder="Acme Corp"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Phone</label>
            <input
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
              placeholder="(555) 123-4567"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Website URL</label>
            <input
              type="url"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
              placeholder="https://example.com"
            />
          </div>
          {activeTab === 'citations' ? (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Zip Code</label>
              <input
                type="text"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                placeholder="90210"
              />
            </div>
          ) : activeTab === 'nap' ? (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Full Address</label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                placeholder="123 Main St, City, ST 12345"
              />
            </div>
          ) : null}
        </div>

        {activeTab === 'citations' && (
          <button
            type="button"
            onClick={handleCitations}
            disabled={loading}
            className="inline-flex items-center gap-2 bg-violet-600 text-white px-4 py-2 rounded-md text-sm hover:bg-violet-700 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            {loading ? 'Discovering…' : 'Discover Citations'}
          </button>
        )}

        {activeTab === 'nap' && (
          <button
            type="button"
            onClick={handleNAP}
            disabled={loading}
            className="inline-flex items-center gap-2 bg-violet-600 text-white px-4 py-2 rounded-md text-sm hover:bg-violet-700 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            {loading ? 'Auditing…' : 'Run NAP Audit'}
          </button>
        )}

        {activeTab === 'locations' && (
          <p className="text-slate-500 text-sm">Multi-location and Bing Places flows use POST /local-seo/locations and /api/bing-places/*.</p>
        )}

        {status && (
          <div className={`mt-4 p-3 rounded-md text-sm ${status.startsWith('Error') ? 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300' : 'bg-violet-50 text-violet-800 dark:bg-violet-950/40 dark:text-violet-200'}`}>
            {status}
          </div>
        )}

        {data && (
          <div className="mt-6">
            <h3 className="mb-3 text-sm font-semibold text-slate-800 dark:text-slate-200">Results</h3>
            <ResultsPanel data={data} />
          </div>
        )}
      </div>
    </motion.div>
  );
}
